import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import _set from 'lodash/set'
import compose from 'recompose/compose'
import { connect as connectRedux, useDispatch, useSelector } from 'react-redux'
import {
  isGuide,
  isEpisode,
  isVideo,
  isSeries,
  getFeaturedContent,
  getLocationType,
} from '../../../services/content/index'
import { renderModal as renderModalAction } from '../../../services/dialog/actions'
import { clearShare as clearShareAction } from '../../../services/share/actions'
import { TYPE_SHARE_V2_SHARE } from '../../../services/dialog/index'
import { refreshComments as refreshCommentsAction } from '../../../services/comments/actions'
import {
  DETAIL_PAGE_STATIC_KEY,
  SERIES_TAB_VALUE,
  EPISODE_TAB_VALUE,
  CURRENT_LESSON_TAB_VALUE,
} from '../../../services/detail/index'
import {
  TYPE_CONTENT_SERIES,
} from '../../../services/content-type/index'
import { SUBSCRIPTION_TYPES } from '../../../services/notifications/index'
import { setEventDefault } from '../../../services/event-tracking/actions'
import { PREVIEW_PLAYED_GA4_EVENT, SHARE_INITIATED_GA4_EVENT } from '../../../services/event-tracking/index'
import { ROLE_ID_AMBASSADOR } from '../../../services/auth-access/index'
import { setFeatureTrackingDataPersistent } from '../../../services/feature-tracking/actions'
import { SHARE_TOOLTIP_MAX_COUNT } from '../../../services/feature-tracking/constants'
import { get as getConfig } from '../../../config/index'
import { createAbsolute } from '../../../services/url/index'
import { copyToClipboard } from '../../../services/share/index'
import { addToasty } from '../../../services/toasty/actions'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { isEntitled } from '../../../services/subscription/index'
import PlaylistAddRemove from '../../PlaylistAddRemove/index'
import PlaylistAddRemoveGraphql from '../../PlaylistAddRemove/PlaylistAddRemoveGraphql/index'
import { NotificationsFollowButton, BUTTON_TYPES } from '../../NotificationsFollowButton/index'
import CommentsLoader from '../../CommentsLoader/index'
import Button, { TYPES } from '../../Button/index'
import ShareTooltipV3 from '../../ShareTooltip/index'
import Comment from '../../Icon/icons/Comment/index'
import Eye from '../../Icon/icons/Eye/index'
import Preview from '../../Icon/icons/Preview/index'
import Link from '../../Icon/icons/Link/index'
import Gift from '../../Icon/icons/Gift/index'
import Stack from '../../Icon/icons/Stack/index'
import LogoSherpa2025 from '../../Icon/icons/LogoSherpa2025/index'
import { setOracleGuideOnSideClosed } from '../../../services/oracle/actions'
import { selectOracleGuideOnSideClosed, selectOracleGuideEnabled } from '../../../services/oracle/selectors'
import { getDetailPageUiElementGQL, getDetailPageUiLocationGQL } from '../utils'

const config = getConfig()
const isPlaylistAddRemoveGraphqlActive = !!_get(config, ['features', 'playlistAddRemoveGraphql'])

/**
 * Detail page action buttons
 * @param {Object} props - The props
 * @param {Map} props.auth - The authorization
 * @param {String} props.activeTabValue - The active tab
 * @param {Map} props.tabData - The content data for the tab
 * @param {Boolean} props.isGuideDetailPage - If it is a Guide
 * @param {Boolean} props.isSeriesDetailPage - If it is a series
 * @param {Boolean} props.isEpisodeDetailPage - If it is an episode
 * @param {Boolean} props.isVideoDetailPage - If it is a video
 * @param {Map} props.staticText - The static text
 * @param {Map} props.comments - The comments
 * @param {String} props.resolverPath - The current url from the resolver
 * @returns {JSX.Element}
 */
const gotsEnabled = !!_get(config, ['features', 'ai', 'oracleGuideOnTheSide'])

function DetailActionButtonsGraphQL (props) {
  const {
    auth,
    activeTabValue,
    tabData,
    isGuideDetailPage,
    isSeriesDetailPage,
    isEpisodeDetailPage,
    isVideoDetailPage,
    staticText,
    comments,
    resolverPath,
    viewportWidth,
  } = props
  const dispatch = useDispatch()
  const commentsData = comments.get('data', Map()) || Map()
  const isSeriesTabActive = activeTabValue === SERIES_TAB_VALUE
  const isEpisodeTabActive = activeTabValue === EPISODE_TAB_VALUE
  const isLessonTabActive = activeTabValue === CURRENT_LESSON_TAB_VALUE
  const featuredContent = getFeaturedContent({ data: tabData })
  const title = isSeriesDetailPage ? featuredContent.get('title') : tabData.get('title')
  const contentType = isSeriesDetailPage ? featuredContent.get('contentType') : tabData.get('contentType')
  const id = isSeriesDetailPage ? featuredContent.get('contentId') : tabData.get('contentId')
  const isMobile = viewportWidth < 944
  const uiElement = getDetailPageUiElementGQL(isEpisodeDetailPage, isSeriesDetailPage)
  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))
  const isAmbassador = auth.get('roleIds', List()).includes(ROLE_ID_AMBASSADOR)
  const canShare = isUserEntitled || isAmbassador
  const geoAllowed = isSeriesDetailPage
    ? featuredContent.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'countryAllowed'])
    : tabData.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'countryAllowed'])
  const hasRelatedCourses = isSeriesDetailPage
    ? featuredContent.get('contentCourses', List()).size > 0
    : tabData.get('contentCourses', List()).size > 0
  let parentSeriesName
  let parentSeriesId

  // If this is a series detail page, parent id and parent title come from the tab data
  if (isSeriesDetailPage) {
    parentSeriesId = tabData.get('contentId')
    parentSeriesName = tabData.get('title')
  }

  // If this is an episode detail page,
  // parent id and parent title come from the series data on the tab data
  if (isEpisodeDetailPage) {
    parentSeriesId = tabData.get('seriesId')
    parentSeriesName = tabData.get('seriesTitle')
  }

  // The detail location type
  const detailLocationType = getLocationType({ data: tabData })
  const uiLocation = getDetailPageUiLocationGQL(detailLocationType)
  const shareButtonRef = useRef(null)
  const followId = parentSeriesId
  let commentContentId = tabData.get('contentId')
  let commentMetaData = tabData || Map()
  let isSharedAllowed
  let hasPreview = tabData.getIn(['previewMedia', 'mediaId'])
  let previewUrl = tabData.get('path')
  let shareId = tabData.get('contentId')
  let shareUrl = tabData.get('path')

  // If this is a series detail page and the episode tab is active
  if (isSeriesDetailPage && (isEpisodeTabActive || isLessonTabActive)) {
    commentContentId = featuredContent.get('contentId')
    commentMetaData = featuredContent || Map()
    isSharedAllowed = featuredContent.getIn(['featureMedia', 'contentPolicy', 'shareAllowed'])
    hasPreview = featuredContent.getIn(['previewMedia', 'mediaId'])
    previewUrl = featuredContent.get('path', '')
    shareId = featuredContent.get('contentId')
    shareUrl = featuredContent.get('path')
  }

  // If this is an episode detail page and the series tab is active
  if (isEpisodeDetailPage && isSeriesTabActive) {
    commentContentId = tabData.get('seriesId')
    commentMetaData = Map({ title: tabData.get('seriesTitle') })
    hasPreview = tabData.getIn(['seriesPreview', 'mediaId'])
    previewUrl = tabData.get('seriesPath')
  }

  // If this is an episode or video detail page and the series tab is not active
  if ((isEpisodeDetailPage || isVideoDetailPage) && !isSeriesTabActive) {
    isSharedAllowed = tabData.getIn(['featureMedia', 'contentPolicy', 'shareAllowed'])
  }

  if (!previewUrl) {
    previewUrl = resolverPath
  }

  const handleShareClick = () => {
    dispatch(renderModalAction(TYPE_SHARE_V2_SHARE, {
      onDismiss: () => dispatch(clearShareAction()),
      contentId: shareId,
      contentUrl: shareUrl,
      contentType,
      parentSeriesId,
      parentSeriesName,
      title,
      uiLocation,
    }))

    // set max tooltip count so it will not be shown again
    dispatch(setFeatureTrackingDataPersistent({
      data: Map({ shareTooltipCount: SHARE_TOOLTIP_MAX_COUNT }),
      auth,
    }))

    // GA4 event
    const ga4EventData = SHARE_INITIATED_GA4_EVENT.mergeDeep({
      content_name: title,
      content_id: id,
      content_type: contentType,
      parent_series_name: parentSeriesName,
      parent_series_id: parentSeriesId,
      ui_location: uiLocation,
    })
    dispatch(setEventDefault(ga4EventData))
  }

  const handleCommentsClick = () => {
    // the comments service expects "id" to be the contentId, so altering it here
    const updatedCommentsMetadata = _set(commentMetaData.toJS(), 'id', commentMetaData.get('contentId'))

    dispatch(refreshCommentsAction({
      contentId: commentContentId,
      commentsId: commentsData.get('id', -1),
      metadata: updatedCommentsMetadata,
      jwt: auth.get('jwt'),
    }))
  }

  const handleShareUrlClick = () => {
    let url = tabData.get('path') || resolverPath

    if (isSeriesDetailPage && !isSeriesTabActive) {
      url = featuredContent.get('path')
    }

    if (isEpisodeDetailPage && isSeriesTabActive) {
      url = tabData.get('seriesPath')
    }

    const absoluteUrl = createAbsolute(url)
    copyToClipboard(absoluteUrl)
    dispatch(addToasty(staticText.get('copied')))
  }

  const handlePreviewClicked = () => {
    dispatch(
      setEventDefault(PREVIEW_PLAYED_GA4_EVENT.mergeDeep({
        content_id: id,
        content_type: contentType,
        content_name: title,
        media_id: tabData.getIn(['preview', 'id']),
        media_language: tabData.get('languageFullText'),
        parent_series_name: parentSeriesName,
        parent_series_id: parentSeriesId,
        is_featured_media: false,
      })),
    )
  }

  const isLoggedIn = getAuthIsLoggedIn(auth)

  const guideOnSideClosed = useSelector(selectOracleGuideOnSideClosed)
  const guideEnabled = useSelector(selectOracleGuideEnabled)
  const resolverQuery = useSelector((state) => state.resolver.get('query'))
  const isAlreadyFullplayer = resolverQuery && resolverQuery.get('fullplayer') === 'feature'

  const aiGuideWatchUrl = isSeriesDetailPage
    ? (featuredContent.get('path', '') || resolverPath)
    : (tabData.get('path') || resolverPath)

  const handleAiGuideClick = () => {
    if (isAlreadyFullplayer) {
      dispatch(setOracleGuideOnSideClosed(!guideOnSideClosed))
    } else {
      dispatch(setOracleGuideOnSideClosed(false))
    }
  }

  const aiGuideButtonComponent = gotsEnabled
    && guideEnabled
    && isLoggedIn
    && !isGuideDetailPage
    && geoAllowed
    ? <Button
        type={TYPES.ICON}
        iconSvg={<LogoSherpa2025 />}
        onClick={handleAiGuideClick}
        url={isAlreadyFullplayer ? undefined : aiGuideWatchUrl}
        query={isAlreadyFullplayer ? undefined : { fullplayer: 'feature' }}
        className="detail-action-buttons__action-button detail-action-buttons__action-button--ai-guide"
        stacked
      >
        <span className="detail-action-buttons__action-label">
          {staticText.get('aiGuide')}
        </span>
      </Button>
    : null

  const playlistButtonComponent = geoAllowed && !isSeriesTabActive && id && !isGuideDetailPage
    ? <PlaylistAddRemove
        className="detail-action-buttons__action-button detail-action-buttons__action-button-playlist"
        contentId={id}
        contentTitle={title}
        ga4EventData={{
          content_id: id,
          content_type: contentType,
          content_name: title,
          media_id: tabData.getIn(['feature', 'id']),
          media_language: tabData.get('languageFullText'),
          is_featured_media: true,
          parent_series_name: parentSeriesName,
          parent_series_id: parentSeriesId,
          ui_location: 'Manage Playlist Modal',
          ui_element: uiElement,
        }}
      />
    : null

  const playlistSeriesButtonComponent = isPlaylistAddRemoveGraphqlActive
    && geoAllowed
    && isSeriesTabActive
    && parentSeriesId
    && !isGuideDetailPage
    ? <PlaylistAddRemoveGraphql
        className="detail-action-buttons__action-button detail-action-buttons__action-button-playlist"
        contentId={parentSeriesId}
        contentTitle={parentSeriesName}
        contentType={TYPE_CONTENT_SERIES}
        ga4EventData={{
          content_id: id,
          content_type: contentType,
          content_name: title,
          media_id: tabData.getIn(['feature', 'id']),
          media_language: tabData.get('languageFullText'),
          is_featured_media: true,
          parent_series_name: parentSeriesName,
          parent_series_id: parentSeriesId,
          ui_location: 'Manage Playlist Modal',
          ui_element: uiElement,
        }}
      />
    : null

  const followButtonComponent = followId && geoAllowed && !isGuideDetailPage
    ? <NotificationsFollowButton
        subscriptionType={isGuideDetailPage
          ? SUBSCRIPTION_TYPES.GUIDE
          : SUBSCRIPTION_TYPES.SERIES}
        type={BUTTON_TYPES.ROUND_SVG}
        contentId={followId}
        disableHover={true}
        uiElement={'Detail Page Primary'}
        uiLocation={uiLocation}
      />
    : null

  const commentsButtonComponent = <>
      <Button
        type={TYPES.ICON}
        iconSvg={<Comment />}
        onClick={handleCommentsClick}
        className="detail-action-buttons__action-button"
        stacked
      >
        <span className="detail-action-buttons__action-label">
          {staticText.get('viewComments')}
        </span>
      </Button>
      <CommentsLoader />
    </>

  const shareButtonComponent = isSharedAllowed
    && geoAllowed
    && canShare
    && shareId
    && !isGuideDetailPage
    && getAuthIsLoggedIn(auth)
    ? <div ref={shareButtonRef}>
        <Button
          type={TYPES.ICON}
          iconSvg={<Gift />}
          className="detail-action-buttons__action-button detail-action-buttons__action-button--royal"
          onClick={handleShareClick}
          stacked
        >
          <span className="detail-action-buttons__action-label detail-action-buttons__action-label--stacked">
            <span className="detail-action-buttons__action-label-text">
              {staticText.get('shareFullVideo')}
            </span>
            <span className="detail-action-buttons__action-label-hint">
              {staticText.get('shareHint')}
            </span>
          </span>
        </Button>
      </div>
    : null

  const shareUrlButtonComponent = !isGuideDetailPage
    ? <Button
        type={TYPES.ICON}
        iconSvg={<Link />}
        onClick={handleShareUrlClick}
        className="detail-action-buttons__action-button"
        stacked
      >
        <span className="detail-action-buttons__action-label">
          {staticText.get('copyLink')}
        </span>
      </Button>
    : null

  const previewButtonComponent = hasPreview
    ? <Button
        type={TYPES.ICON}
        iconSvg={isGuideDetailPage ? <Eye /> : <Preview />}
        url={previewUrl}
        query={{ fullplayer: 'preview' }}
        className={`detail-action-buttons__action-button${isGuideDetailPage ? ' detail-action-buttons__action-button--guide' : ''}`}
        stacked
        onClick={handlePreviewClicked}
      >
        <span className="detail-action-buttons__action-label">
          {isGuideDetailPage ? staticText.get('introduction') : staticText.get('preview')}
        </span>
      </Button>
    : null

  const relatedCoursesButtonComponent = hasRelatedCourses && isMobile && isEpisodeTabActive
    ? <Button
        type={TYPES.ICON}
        iconSvg={<Stack />}
        url={`${tabData.get('path')}#relatedCourses`}
        className="detail-action-buttons__action-button"
        stacked
      >
        <span className="detail-action-buttons__action-label">
          {staticText.get('viewCourses')}
        </span>
      </Button>
    : null

  const renderButtonsArray = (buttonsArray) => {
    return (
      <>
        {buttonsArray.map((actionButton, key) => {
          return (
            <React.Fragment key={key}>
              {actionButton}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const renderButtons = () => {
    // creating components array and filtering out nullish components
    const actionButtons = [
      aiGuideButtonComponent,
      playlistButtonComponent,
      relatedCoursesButtonComponent,
      playlistSeriesButtonComponent,
      followButtonComponent,
      commentsButtonComponent,
      shareUrlButtonComponent,
    ].filter((btn) => !!btn)

    // if previewButtonComponent exists it should be added to the head of array
    // for guides, for other types it should be added to the tail
    if (previewButtonComponent) {
      if (isGuideDetailPage) {
        actionButtons.unshift(previewButtonComponent)
      } else {
        actionButtons.push(previewButtonComponent)
      }
    }

    // Push share button to the end of the array
    if (shareButtonComponent) {
      actionButtons.push(shareButtonComponent)
    }

    if (isMobile) {
      // if shareButtonComponent exists we need to push it to the first column
      // so make the first array greater (or equal) size than the second one
      const firstColumn = actionButtons.slice(0, Math.ceil(actionButtons.length / 2))
      const secondColumn = actionButtons.slice(Math.ceil(actionButtons.length / 2))

      return (
        <>
          <div className="detail-action-buttons__column">
            {renderButtonsArray(firstColumn)}
          </div>
          <div className="detail-action-buttons__column">
            {renderButtonsArray(secondColumn)}
          </div>
        </>
      )
    }

    return renderButtonsArray(actionButtons)
  }

  return (
    <div className="detail-action-buttons">
      {renderButtons()}
      <ShareTooltipV3
        handleShareClick={handleShareClick}
        buttonRef={shareButtonRef}
        isMobile={isMobile}
      />
    </div>
  )
}

DetailActionButtonsGraphQL.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  tabData: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  isVideoDetailPage: PropTypes.bool,
  isEpisodeDetailPage: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  isGuideDetailPage: PropTypes.bool,
  activeTabValue: PropTypes.string,
  resolverPath: PropTypes.string,
  viewportWidth: PropTypes.number,
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        auth,
        app,
        comments,
        resolver,
      } = state
      const { tabData = Map() } = props
      const contentType = tabData.get('contentType')
      const contentSubtype = tabData.get('contentSubtype')
      const displayType = tabData.get('displayType')
      const data = tabData

      return {
        auth,
        comments,
        staticText: state.staticText.getIn(['data', DETAIL_PAGE_STATIC_KEY, 'data']),
        isSeriesDetailPage: isSeries({ contentType }),
        isVideoDetailPage: isVideo({ contentType, data }),
        isEpisodeDetailPage: isEpisode({ contentType, data }),
        isGuideDetailPage: isGuide({ contentSubtype, displayType }),
        resolverPath: resolver.get('path'),
        viewportWidth: app.getIn(['viewport', 'width'], 0),
      }
    },
  ),
)(DetailActionButtonsGraphQL)
